<template>
    <div class="row">
        <template v-if="item.debit == null">
            <label for="formFile" class="form-label">Transfer from/Paid from</label>
            <div class="cols-12 col-sm-6 col-lg-3">
                <v-select
                    placeholder="Select Cash & Bank Account"
                    v-model="item.account_head_id"
                    :options="accountHeads"
                    :get-option-label="formatAccountHeadLabel"
                    :reduce="name => name.id"
                />
            </div>
            <div class="cols-12 col-sm-6 col-lg-3">
                <v-select
                    placeholder="Select Project"
                    v-model="item.project_id"
                    :options="projects"
                    label="name"
                    :reduce="name => name.id"
                    @option:selected="onSelectProject"
                />
            </div>
            <div class="cols-12 col-sm-6 col-lg-3">
                <v-select
                    placeholder="Select Donor"
                    class="w-100"
                    :options="donors"
                    label="name"
                    :reduce="name => name.id"
                    v-model="item.donor_id"
                />
            </div>
            <div class="cols-12 col-sm-6 col-lg-3">
                <v-select
                    placeholder="Select Cost Centre"
                    v-model="item.cost_centre_id"
                    :options="costCentres"
                    label="name"
                    :reduce="name => name.id"
                />
            </div>
            <div class="cols-12 col-sm-6 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Business</label>
              <v-select
                  placeholder="Select Business"
                  class="w-100"
                  :options="businesses"
                  label="name"
                  :reduce="name => name.id"
                  v-model="item.business_id"
              />
            </div>
            <div class="cols-12 col-sm-6 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Party</label>
              <div class="d-flex align-items-center gap-2">
                <div class="flex-grow-1 party-select">
                  <AsyncSelect
                      placeholder="Select Party"
                      v-model="item.party"
                      :api-service="fetchContactProfiles"
                      :additional-query="partyQuery"
                      :format-label="formatPatientLabel"
                  />
                </div>
                <button v-if="onAddParty" type="button" class="btn btn-primary btn-sm" title="Add New Party" data-customize-key="add_party" data-customize-label="Add Party" @click="onAddParty(item)"><i class="fa fa-plus"></i></button>
              </div>
            </div>
            <div class="col-6 mt-1">
                <label for="formFileMultiple" class="form-label">Description</label>
                <input
                    v-model="item.note"
                    type="text" class="form-control"
                    placeholder="Description"
                >
            </div>
        </template>
        <template v-else>
            <label for="formFile" class="form-label">Transfer to/Received in</label>
            <div class="cols-12 col-sm-6 col-lg-3">
                <v-select
                    placeholder="Select Cash & Bank Account"
                    v-model="item.account_head_id"
                    :options="accountHeads"
                    :get-option-label="formatAccountHeadLabel"
                    :reduce="name => name.id"
                />
            </div>
            <div class="cols-12 col-sm-6 col-lg-3">
                <v-select
                    placeholder="Select Project"
                    v-model="item.project_id"
                    :options="projects"
                    label="name"
                    :reduce="name => name.id"
                    @option:selected="onSelectProject"
                />
            </div>
            <div class="cols-12 col-sm-6 col-lg-3">
                <v-select
                    placeholder="Select Donor"
                    class="w-100"
                    :options="donors"
                    label="name"
                    :reduce="name => name.id"
                    v-model="item.donor_id"
                />
            </div>
            <div class="cols-12 col-sm-6 col-lg-3">
                <v-select
                    placeholder="Select Cost Centre"
                    v-model="item.cost_centre_id"
                    :options="costCentres"
                    label="name"
                    :reduce="name => name.id"
                />
            </div>
            <div class="cols-12 col-sm-6 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Business</label>
              <v-select
                  placeholder="Select Business"
                  class="w-100"
                  :options="businesses"
                  label="name"
                  :reduce="name => name.id"
                  v-model="item.business_id"
              />
            </div>
            <div class="cols-12 col-sm-6 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Party</label>
              <div class="d-flex align-items-center gap-2">
                <div class="flex-grow-1 party-select">
                  <AsyncSelect
                      placeholder="Select Party"
                      v-model="item.party"
                      :api-service="fetchContactProfiles"
                      :additional-query="partyQuery"
                      :format-label="formatPatientLabel"
                  />
                </div>
                <button v-if="onAddParty" type="button" class="btn btn-primary btn-sm" title="Add New Party" data-customize-key="add_party" data-customize-label="Add Party" @click="onAddParty(item)"><i class="fa fa-plus"></i></button>
              </div>
            </div>
            <div class="col-6 mt-1">
                <label for="formFileMultiple" class="form-label">Description</label>
                <input
                    v-model="item.note"
                    type="text" class="form-control"
                    placeholder="Description"
                >
            </div>
        </template>
    </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleContact from "@/services/modules/contact";
import { formatAccountHeadLabel } from "@/services/utils/global";
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";

const $props = defineProps({
    onAddParty: {
        type: Function,
        default: null
    },
    item: {
        type: Object
    },
    index: {
        type: Number
    },
    accountHeads: {
        type: Array
    },
    projects: {
        type: Array
    },
    costCentres: {
        type: Array
    },
    contactProfiles: {
        type: Array
    },
    businesses: {
      type: Array,
      default: () => [],
    },
    isEdit: {
        type: Boolean,
        default: false
    },
})

const {fetchContactProfiles} = handleContact()
const { formatPatientLabel } = useAsyncDropdownHelper();

const donors = ref([]);
const projectId = computed(() => $props.item.project_id)
const party = computed(() => $props.item.party);
const partyQuery = computed(() => {
  if ($props.isEdit) return {}
  return {status: 'active'}
});
//watchers
watch(party, (newVal, oldVal) => {
  if (newVal == null) {
    return $props.item.contact_profile_id = null
  }
  return $props.item.contact_profile_id = newVal.id
});
watch( projectId, (newValue) => {
    if(newValue === null) {
        donors.value = [];
        $props.item.donor_id = null;
    }
})
const onSelectProject = (reset = true) => {
    if(reset) {
        $props.item.donor_id = null;
    }
    donors.value = [];
    for (let project of $props.projects) {
        if(project.id === $props.item.project_id && project.party.hasOwnProperty('id')){
            donors.value.push(project.party);
            break;
        }
    }
}
onMounted( () => {
    if($props.item.donor_id)
        onSelectProject(false)
})
</script>
<style scoped>
.journal-line{
        border: 1px solid #f0f2f5;
    }
    .journal-line:hover{
        background: #f0f2f5;
    }
    .party-select {
        min-width: 0;
    }
</style>
